// Links log data to Raider.IO data.
//
// Nothing here guesses. A Mythic+ run from the log is only tied to a Raider.IO
// run when the language independent ids agree (challenge mode id, keystone
// level) and the in-game clear time matches to the second. Anything weaker is
// reported as a candidate for the user to confirm, or not linked at all.

import { getMythicPlusStaticData, getAffixes } from '../raiderApi'

const CLEAR_TIME_TOLERANCE_MS = 2500
const COMPLETION_TOLERANCE_MS = 20 * 60 * 1000

export function normalizeRealm(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/['’\s_-]/g, '')
}

export function normalizeName(value) {
    return String(value || '').trim().toLowerCase()
}

// Builds challengeModeId -> dungeon from the Raider.IO static data, which is the
// only trustworthy way to turn a log's numeric dungeon id into a name, a slug,
// an icon and the keystone timer.
export async function loadDungeonIndex(expansionId = 11, options = {}) {
    const staticData = await getMythicPlusStaticData(expansionId, options)
    const index = new Map()
    const seasons = staticData.seasons || []

    const collect = (dungeons) => {
        (dungeons || []).forEach((dungeon) => {
            const id = Number(dungeon.challenge_mode_id)
            if (!id || index.has(id)) return
            index.set(id, {
                challengeModeId: id,
                zoneId: dungeon.id,
                slug: dungeon.slug,
                name: dungeon.name,
                shortName: dungeon.short_name,
                keystoneTimerMs: Number(dungeon.keystone_timer_seconds || 0) * 1000,
                iconUrl: dungeon.icon_url,
                backgroundUrl: dungeon.background_image_url,
            })
        })
    }

    collect(staticData.dungeons)
    seasons.forEach((season) => collect(season.dungeons))
    return index
}

export async function loadCurrentAffixes(region = 'eu', locale = 'en', options = {}) {
    try {
        const response = await getAffixes(region, locale, options)
        const index = new Map()
        ;(response.affix_details || []).forEach((affix) => {
            if (affix && affix.id) index.set(Number(affix.id), affix.name)
        })
        return index
    } catch (error) {
        return new Map()
    }
}

export function describeKeystoneResult(logRun, dungeon) {
    const timerMs = dungeon && dungeon.keystoneTimerMs ? dungeon.keystoneTimerMs : 0
    const totalTimeMs = logRun && logRun.meta ? Number(logRun.meta.totalTimeMs || 0) : 0

    if (!totalTimeMs || !timerMs) {
        return { upgrades: null, overTime: null, timerMs, totalTimeMs }
    }

    if (totalTimeMs > timerMs) {
        return { upgrades: 0, overTime: true, timerMs, totalTimeMs }
    }

    const ratio = totalTimeMs / timerMs
    const upgrades = ratio <= 0.6 ? 3 : ratio <= 0.8 ? 2 : 1
    return { upgrades, overTime: false, timerMs, totalTimeMs }
}

// Decides whether a character from the dashboard is the character that recorded
// the log. The log only gives a localized realm with its spaces removed, so the
// comparison normalizes both sides instead of resolving a Raider.IO realm slug.
export function matchesLogOwner(logOwner, profile) {
    if (!logOwner || !profile) return { matches: false, score: 0, reasons: [] }

    const reasons = []
    let score = 0

    if (normalizeName(logOwner.name) === normalizeName(profile.name)) {
        score += 2
        reasons.push('character name')
    } else {
        return { matches: false, score: 0, reasons: [] }
    }

    if (logOwner.realm && profile.realm && normalizeRealm(logOwner.realm) === normalizeRealm(profile.realm)) {
        score += 2
        reasons.push('realm')
    }

    if (logOwner.region && profile.region && logOwner.region.toLowerCase() === String(profile.region).toLowerCase()) {
        score += 1
        reasons.push('region')
    }

    return { matches: score >= 4, score, reasons }
}

export function findOwnerCandidates(logOwner, savedCharacters) {
    if (!logOwner) return []
    return (savedCharacters || [])
        .map((entry) => {
            const profile = entry.profile || entry
            const match = matchesLogOwner(logOwner, profile)
            return { entry, profile, ...match }
        })
        .filter((candidate) => candidate.score >= 2)
        .sort((left, right) => right.score - left.score)
}

// Matches one Mythic+ run from a log against a character's Raider.IO runs.
// `exact` means every strong signal agreed; `candidate` means the dungeon and
// level agreed but the timing did not line up well enough to claim identity.
export function matchChallengeRun(logRun, raiderRuns) {
    const meta = (logRun && logRun.meta) || {}
    const challengeModeId = Number(meta.challengeModeId || 0)
    const keystoneLevel = Number(meta.keystoneLevel || 0)
    const totalTimeMs = Number(meta.totalTimeMs || 0)
    const endTs = Number(logRun && logRun.endTs) || 0

    if (!challengeModeId || !Array.isArray(raiderRuns) || !raiderRuns.length) {
        return { status: 'none', run: null, reasons: [] }
    }

    const sameDungeon = raiderRuns.filter((run) => Number(run.map_challenge_mode_id) === challengeModeId)
    if (!sameDungeon.length) return { status: 'none', run: null, reasons: [] }

    const sameLevel = keystoneLevel
        ? sameDungeon.filter((run) => Number(run.mythic_level) === keystoneLevel)
        : sameDungeon
    if (!sameLevel.length) return { status: 'none', run: null, reasons: [] }

    const scored = sameLevel.map((run) => {
        const reasons = ['dungeon id', 'keystone level']
        let score = 2
        const completedAt = Date.parse(run.completed_at || '') || 0
        const clearTimeMs = Number(run.clear_time_ms || 0)

        if (totalTimeMs && clearTimeMs && Math.abs(clearTimeMs - totalTimeMs) <= CLEAR_TIME_TOLERANCE_MS) {
            score += 3
            reasons.push('clear time')
        }
        if (endTs && completedAt && Math.abs(completedAt - endTs) <= COMPLETION_TOLERANCE_MS) {
            score += 2
            reasons.push('completion time')
        }

        const logAffixes = Array.isArray(meta.affixIds) ? [...meta.affixIds].sort().join(',') : ''
        const runAffixes = Array.isArray(run.affixes) ? run.affixes.map((affix) => Number(affix.id)).sort().join(',') : ''
        if (logAffixes && runAffixes && logAffixes === runAffixes) {
            score += 1
            reasons.push('affixes')
        }

        return { run, score, reasons }
    })

    scored.sort((left, right) => right.score - left.score)
    const best = scored[0]
    const runnerUp = scored[1]

    // Two runs of the same dungeon and level that both look right mean the log
    // cannot identify which one it is; that needs a human, not a coin flip.
    if (best.score >= 5 && (!runnerUp || runnerUp.score < best.score)) {
        return { status: 'exact', run: best.run, reasons: best.reasons, score: best.score }
    }

    return {
        status: 'candidate',
        run: null,
        candidates: scored.slice(0, 3),
        reasons: best.reasons,
        score: best.score,
    }
}
